import type {ReactNode} from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import {DARK_BAND_BACKGROUND} from "../Setup/Mui";

export type SectionColor = 'dark' | 'light' | 'grey'

/**
 * A full-bleed band: one tone, one vertical rhythm, its own horizontal padding — so it wraps a Container rather than sitting inside one.
 * `color` is OPTIONAL (it always had a runtime default; the old required-prop interface, which also shadowed this function's name, broke every wrapper that forwarded an optional color).
 * `hero` adds the 60vh floor and defaults to "every dark band", which is what the legal pages' dark title hero relies on. A dark band that is NOT a hero must pass `hero={false}` — `app/routes/splash-sections`'s `Band` always passes an explicit value.
 * The `dark` foreground is `common.white` and must NEVER be `primary.contrastText`: `DARK_BAND_BACKGROUND` is scheme-invariant navy, but `primary.contrastText` flips to `#0A1128` in the dark color scheme — navy ON navy, 1.0:1, every heading on every band silently invisible to anyone who has ever chosen dark mode. `src/ui/Footer.tsx` carries the same warning for the same reason.
 */
export function Section({children, color = 'light', hero = color === 'dark'}: {children?: ReactNode; color?: SectionColor; hero?: boolean}) {
  const sxProperties = {
    dark: {
      background: DARK_BAND_BACKGROUND,
      color: 'common.white'
    },
    light: {
      backgroundColor: 'background.paper'
    },
    grey: {
      backgroundColor: 'background.default'
    }
  }[color];

  return (
    <Box sx={{ ...sxProperties, overflow: 'hidden' }}>
      <Stack
        direction="column"
        spacing={{xs: 4, md: 6}}
        sx={{
          alignItems: "center",
          py: {xs: 6, md: 8, lg: 12},
          px: {xs: 3, lg: 6},
          minHeight: hero ? '60vh' : 'auto',
          justifyContent: 'center'
        }}>
        {children}
      </Stack>
    </Box>
  );
}
